import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, X, Search, Check, ChevronDown, ChevronRight, BookOpen } from 'lucide-react';
import AdminPageHeader from '../../components/admin/AdminPageHeader';
import client from '../../api/client';
import { adminApi } from '../../api/admin';
import toast from 'react-hot-toast';

const ICONS = ['📋','📊','📝','🏥','💊','📑','⚖️','📞','✉️','📁','🔔','💰','🏠','🚗','👤','📌'];

const FIELD_TYPE_COLORS = {
  TEXT: 'bg-blue-50 text-blue-700', NUMBER: 'bg-green-50 text-green-700',
  CURRENCY: 'bg-emerald-50 text-emerald-700', DATE: 'bg-purple-50 text-purple-700',
  DROPDOWN: 'bg-orange-50 text-orange-700', CHECKBOX: 'bg-gray-100 text-gray-700',
  TEXTAREA: 'bg-blue-50 text-blue-700', EMAIL: 'bg-pink-50 text-pink-700',
  PHONE: 'bg-teal-50 text-teal-700', ADDRESS: 'bg-indigo-50 text-indigo-700',
  SSN: 'bg-red-50 text-red-700', BOOLEAN: 'bg-gray-100 text-gray-700',
};

export default function DiaryTypesManager() {
  const [diaryTypes, setDiaryTypes] = useState([]);
  const [recordTypes, setRecordTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', icon: '📋', color: '#6B7280', record_type_id: '' });

  // Field picker state
  const [showFieldPicker, setShowFieldPicker] = useState(null);
  const [fieldGroups, setFieldGroups] = useState({ direct_fields: [], linked_field_sets: [] });
  const [trackedFields, setTrackedFields] = useState([]);
  const [fieldSearch, setFieldSearch] = useState('');
  const [expandedGroups, setExpandedGroups] = useState({});

  useEffect(() => {
    loadData();
    adminApi.getRecordTypes().then(r => setRecordTypes(r.data.record_types || [])).catch(() => {});
  }, []);

  const loadData = async () => {
    try {
      const { data } = await client.get('/diary/types');
      setDiaryTypes(data.diary_types || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!form.name) return toast.error('Name is required');
    if (!form.record_type_id) return toast.error('Record type is required');
    try {
      if (editing) {
        await client.put(`/diary/types/${editing.id}`, form);
        toast.success('Updated');
      } else {
        await client.post('/diary/types', form);
        toast.success('Created');
      }
      setShowModal(false); setEditing(null);
      setForm({ name: '', description: '', icon: '📋', color: '#6B7280', record_type_id: '' });
      loadData();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  const handleEdit = (dt) => {
    setEditing(dt);
    setForm({ name: dt.name, description: dt.description || '', icon: dt.icon, color: dt.color, record_type_id: dt.record_type_id || '' });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this diary type?')) return;
    try { await client.delete(`/diary/types/${id}`); toast.success('Deleted'); loadData(); }
    catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  const openFieldPicker = async (dt) => {
    if (!dt.record_type_id) return toast.error('Link this diary to a record type first');
    setShowFieldPicker(dt.id);
    setTrackedFields(dt.tracked_fields || []);
    setFieldSearch('');
    setExpandedGroups({ direct: true }); // expand direct by default
    try {
      const { data } = await client.get(`/document-templates/available-fields/${dt.record_type_id}`);
      setFieldGroups(data);
    } catch (err) { toast.error('Failed to load fields'); }
  };

  const toggleField = (fieldId) => {
    setTrackedFields(prev =>
      prev.includes(fieldId) ? prev.filter(f => f !== fieldId) : [...prev, fieldId]
    );
  };

  const toggleGroup = (groupKey) => {
    setExpandedGroups(prev => ({ ...prev, [groupKey]: !prev[groupKey] }));
  };

  const selectAllInGroup = (fields) => {
    const ids = fields.map(f => f.id);
    const allSelected = ids.every(id => trackedFields.includes(id));
    if (allSelected) {
      setTrackedFields(prev => prev.filter(id => !ids.includes(id)));
    } else {
      setTrackedFields(prev => [...new Set([...prev, ...ids])]);
    }
  };

  const saveTrackedFields = async () => {
    try {
      await client.put(`/diary/types/${showFieldPicker}`, { tracked_fields: trackedFields });
      toast.success('Tracked fields saved! Changes to these fields will auto-log.');
      setShowFieldPicker(null);
      loadData();
    } catch (err) { toast.error('Failed to save'); }
  };

  const matchesSearch = (field) => {
    if (!fieldSearch) return true;
    const q = fieldSearch.toLowerCase();
    return field.name?.toLowerCase().includes(q) || field.field_type?.toLowerCase().includes(q);
  };

  // Build all field groups for the picker
  const buildFieldGroups = () => {
    const groups = [];

    // Direct fields
    const directFiltered = (fieldGroups.direct_fields || []).filter(matchesSearch);
    if (directFiltered.length > 0) {
      const rtName = directFiltered[0]?.record_type_name || 'Record';
      groups.push({
        key: 'direct',
        label: rtName + ' Fields',
        icon: '📋',
        fields: directFiltered
      });
    }

    // Linked record sets (Contact, Company, etc.)
    for (const linked of (fieldGroups.linked_field_sets || [])) {
      const linkedFiltered = (linked.fields || []).filter(matchesSearch);
      if (linkedFiltered.length > 0) {
        groups.push({
          key: 'linked-' + linked.link_field_id,
          label: linked.link_label + ' (' + linked.target_record_type + ')',
          icon: linked.target_record_type?.toLowerCase().includes('contact') ? '👤' :
                linked.target_record_type?.toLowerCase().includes('company') ? '🏢' : '🔗',
          fields: linkedFiltered
        });
      }
    }

    return groups;
  };

  // Resolve field label from all groups
  const resolveFieldLabel = (fieldId, dt) => {
    if (dt.tracked_field_labels && dt.tracked_field_labels[fieldId]) {
      return dt.tracked_field_labels[fieldId];
    }
    return fieldId.slice(0, 8) + '...';
  };

  return (
    <div className="max-w-4xl mx-auto">
      <AdminPageHeader title="Diary Types" description="Configure diary categories and auto-tracked fields" icon={BookOpen} iconColor="text-amber-600" iconBg="bg-amber-50">
        <button onClick={() => { setEditing(null); setForm({ name: '', description: '', icon: '📋', color: '#6B7280', record_type_id: '' }); setShowModal(true); }}
          className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
          <Plus className="w-4 h-4" /> New Diary Type
        </button>
      </AdminPageHeader>

      <div className="bg-white rounded-xl border border-gray-200">
        {loading ? (
          <div className="text-center py-12 text-gray-400">Loading...</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {diaryTypes.map(dt => (
              <div key={dt.id} className="px-5 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{dt.icon}</span>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900">{dt.name}</span>
                        {dt.is_system && <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">System</span>}
                        {dt.record_type_name && <span className="text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">{dt.record_type_name}</span>}
                      </div>
                      {dt.description && <p className="text-xs text-gray-500 mt-0.5">{dt.description}</p>}
                    </div>
                  </div>
                  {!dt.is_system && (
                    <div className="flex items-center gap-1">
                      <button onClick={() => openFieldPicker(dt)}
                        className="px-3 py-1.5 text-xs text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition">
                        {(dt.tracked_fields || []).length > 0
                          ? `${(dt.tracked_fields || []).length} fields tracked`
                          : 'Add tracked fields'}
                      </button>
                      <button onClick={() => handleEdit(dt)} className="p-2 text-gray-400 hover:text-blue-600 rounded hover:bg-blue-50">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDelete(dt.id)} className="p-2 text-gray-400 hover:text-red-600 rounded hover:bg-red-50">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
                {/* Tracked field labels */}
                {(dt.tracked_fields || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2 ml-11">
                    {(dt.tracked_fields || []).map(fId => (
                      <span key={fId} className="text-[10px] bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">
                        {resolveFieldLabel(fId, dt)}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-gray-100">
              <h3 className="font-semibold text-gray-900">{editing ? 'Edit' : 'New'} Diary Type</h3>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Name *</label>
                <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Medical Diary" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                <input type="text" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                  placeholder="Brief description..." className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Icon</label>
                <div className="flex flex-wrap gap-1.5">
                  {ICONS.map(icon => (
                    <button key={icon} onClick={() => setForm({ ...form, icon })}
                      className={`w-9 h-9 text-lg rounded-lg border transition ${form.icon === icon ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                      {icon}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Linked Record Type *</label>
                <select value={form.record_type_id} onChange={e => setForm({ ...form, record_type_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm">
                  <option value="">Select record type...</option>
                  {recordTypes.map(rt => <option key={rt.id} value={rt.id}>{rt.name}</option>)}
                </select>
                <p className="text-[10px] text-gray-400 mt-1">Diary appears on records of this type. Fields from linked contacts/companies also available.</p>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleSave} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                {editing ? 'Save' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Field Picker Modal */}
      {showFieldPicker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowFieldPicker(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-gray-100">
              <h3 className="font-semibold text-gray-900">Select Tracked Fields</h3>
              <p className="text-xs text-gray-500 mt-1">Changes to selected fields will auto-log to this diary</p>
            </div>

            {/* Search */}
            <div className="px-5 py-3 border-b border-gray-50">
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400" />
                <input type="text" value={fieldSearch} onChange={e => setFieldSearch(e.target.value)}
                  placeholder="Search fields..." className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm" />
              </div>
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-gray-400">{trackedFields.length} fields selected</span>
                <button onClick={() => setTrackedFields([])} className="text-[10px] text-gray-500 hover:underline">Clear all</button>
              </div>
            </div>

            {/* Grouped Fields */}
            <div className="flex-1 overflow-y-auto">
              {buildFieldGroups().map(group => {
                const isExpanded = expandedGroups[group.key] !== false; // default expanded
                const selectedCount = group.fields.filter(f => trackedFields.includes(f.id)).length;
                return (
                  <div key={group.key} className="border-b border-gray-50">
                    {/* Group Header */}
                    <button onClick={() => toggleGroup(group.key)}
                      className="w-full flex items-center justify-between px-5 py-2.5 hover:bg-gray-50 transition">
                      <div className="flex items-center gap-2">
                        {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                        <span className="text-base">{group.icon}</span>
                        <span className="text-sm font-medium text-gray-800">{group.label}</span>
                        <span className="text-[10px] text-gray-400">{group.fields.length} fields</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {selectedCount > 0 && (
                          <span className="text-[10px] bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded-full">{selectedCount} selected</span>
                        )}
                        <button onClick={(e) => { e.stopPropagation(); selectAllInGroup(group.fields); }}
                          className="text-[10px] text-blue-600 hover:underline px-1">
                          {selectedCount === group.fields.length ? 'Deselect all' : 'Select all'}
                        </button>
                      </div>
                    </button>

                    {/* Fields */}
                    {isExpanded && (
                      <div className="px-3 pb-2">
                        {group.fields.map(field => {
                          const isTracked = trackedFields.includes(field.id);
                          const typeColor = FIELD_TYPE_COLORS[field.field_type] || 'bg-gray-100 text-gray-600';
                          return (
                            <button key={field.id} onClick={() => toggleField(field.id)}
                              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition ${
                                isTracked ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-50 border border-transparent'
                              }`}>
                              <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${
                                isTracked ? 'bg-blue-600' : 'border-2 border-gray-300'
                              }`}>
                                {isTracked && <Check className="w-3 h-3 text-white" />}
                              </div>
                              <span className="text-sm text-gray-900 flex-1">{field.name}</span>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded ${typeColor}`}>{field.field_type}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}

              {buildFieldGroups().length === 0 && (
                <div className="text-center py-8 text-gray-400 text-sm">No fields found</div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100">
              <button onClick={() => setShowFieldPicker(null)}
                className="px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={saveTrackedFields}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                Save ({trackedFields.length} fields)
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
